import {
  Button,
  ListContainer,
  ListContext,
  PAGE_DEFAULT,
  PAGE_SIZE_UNLIMITED,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  formatSchoolYear,
  pn
} from "@compass/core";
import { ListBeProperty } from "components";
import {
  NscCollegeListDto,
  NscCollegeListFilter,
  NscCollegeListResource,
  Permission,
  PermissionAction,
} from "generated/openApi";
import { useList, useRouter } from "hooks";
import { nscCollegePageRoute } from "pages/students/nsc-colleges/NscCollegePage/NscCollegePage";
import { useEffect, useState } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import { isAuthorized } from "services";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import SchoolIcon from "@mui/icons-material/School";
import ApiService from "services/apiService";

export const NscColleges = (props: { initialLoad: boolean; identification: string; onGetList: () => void }) => {
  const ROOT_PERMISSION: Permission = Permission.StudentNscCollege;
  const navigate = useNavigate();

  const { matchTemplatePath } = useRouter();

  const [listFilter, setListFilter] = useState<NscCollegeListFilter>({
    nscCollegeFilter: { identification: props.identification, recordFound: true },
    page: PAGE_DEFAULT,
    pageSize: PAGE_SIZE_UNLIMITED,
    sortObjects: [{ property: pn<NscCollegeListDto>("year"), isDesc: true }],
  });
  const [listItems, setListItems] = useState<NscCollegeListDto[]>([]);
  const [resource, setResource] = useState<NscCollegeListResource>({});
  const properties = resource.properties || [];

  const { tableContainerRef, loadList, applySort } = useList({
    listFilter: listFilter,
    setListFilter: setListFilter,
    fetchList: (listFilter) => {
      ApiService.nscColleges.getList(listFilter).then((response) => {
        setListItems(response);
      });
    },
  });

  useEffect(() => {
    if (props.initialLoad) {
      loadList(listFilter);

      ApiService.nscColleges.getListResource().then((response) => {
        setResource(response);
      });
    }
  }, [props.initialLoad]);

  const header = (
    <Stack direction="row" justifyContent="space-between" alignItems="center">
      <Typography variant="inherit">Post-Secondary</Typography>

      {isAuthorized(ROOT_PERMISSION, PermissionAction.Create) && (
        <Button
          onClick={() =>
            navigate({
              pathname: nscCollegePageRoute.getPath({
                id: 0,
                crudMode: "edit",
                screenType: "dialog",
              }),
              search: nscCollegePageRoute.getOptionalParams?.({
                identification: props.identification,
              }),
            })
          }
          variant="outlined"
        >
          + New
        </Button>
      )}
    </Stack>
  );

  return (
    <>
      <ListContainer
        fullHeight
        header={header}
        tableContainer={
          <TableContainer ref={tableContainerRef}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <ListBeProperty
                      listProperty={properties.find((x) => x.propertyName === pn<NscCollegeListDto>("year"))}
                      sortObjects={listFilter.sortObjects}
                      onChange={(sortObjects) => applySort(sortObjects)}
                    />
                  </TableCell>

                  <TableCell>
                    <ListBeProperty
                      listProperty={properties.find((x) => x.propertyName === pn<NscCollegeListDto>("recordFound"))}
                      sortObjects={listFilter.sortObjects}
                      onChange={(sortObjects) => applySort(sortObjects)}
                    />
                  </TableCell>

                  <TableCell>
                    <ListBeProperty
                      listProperty={properties.find((x) => x.propertyName === pn<NscCollegeListDto>("term"))}
                      sortObjects={listFilter.sortObjects}
                      onChange={(sortObjects) => applySort(sortObjects)}
                    />
                  </TableCell>

                  <TableCell>
                    <ListBeProperty
                      listProperty={properties.find((x) => x.propertyName === pn<NscCollegeListDto>("collegeName"))}
                      sortObjects={listFilter.sortObjects}
                      onChange={(sortObjects) => applySort(sortObjects)}
                    />
                  </TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {listItems?.map((listItem) => (
                  <TableRow
                    key={listItem.id}
                    onClick={() =>
                      navigate({
                        pathname: nscCollegePageRoute.getPath({
                          id: listItem.id,
                          crudMode: "view",
                          screenType: "dialog",
                        }),
                      })
                    }
                  >
                    <TableCell>{formatSchoolYear(listItem.term == "Fall" ? listItem.year : listItem.year! - 1)}</TableCell>
                    <TableCell>{listItem.recordFound ? <CheckCircleIcon /> : <></>}</TableCell>
                    <TableCell>{listItem.term} {listItem.year}</TableCell>
                    <TableCell>{listItem.collegeName}</TableCell>
                    <TableCell type="action">
                      <Stack direction="row" spacing="sm" alignItems="center">
                        {listItem.graduationDate ? <SchoolIcon /> : <></>}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        }
      />

      <ListContext.Provider
        value={{
          loadList: (options) => {
            loadList(listFilter, { resetPage: options?.resetPage });
            props.onGetList();
          },
        }}
      >
        {matchTemplatePath(nscCollegePageRoute.templatePath) && <Outlet />}
      </ListContext.Provider>
    </>
  );
};
